import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, Link, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API, getAuthHeaders } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  ShoppingCart, Search, Menu, X, Plus, Minus, ChevronDown,
  User, LogOut, Package, MapPin, Phone, Filter, Grid3X3, List,
  Building2, AlertCircle, Shield, Truck, Heart, MessageCircle, Loader2
} from 'lucide-react';
import AddressAutocomplete from '../components/AddressAutocomplete';
import Seo from '../components/Seo';
import { pickMeta } from '../seoMeta';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetFooter,
} from '../components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Slider } from '../components/ui/slider';
import { Label } from '../components/ui/label';
import LanguageSwitcher from '../components/LanguageSwitcher';
import NotificationBell from '../components/NotificationBell';

function Catalog({ user, onLogout, setUser }) {
  const { t, i18n } = useTranslation();
  const { lang } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const currentLang = lang || i18n.language || 'ru';

  // Check if admin is viewing as client
  const isAdminViewingAsClient = localStorage.getItem('admin_token_backup') !== null;
  const adminEmailBackup = localStorage.getItem('admin_email_backup');

  // Return to admin function
  const handleReturnToAdmin = () => {
    const adminToken = localStorage.getItem('admin_token_backup');
    if (adminToken) {
      localStorage.setItem('token', adminToken);
      localStorage.removeItem('admin_token_backup');
      localStorage.removeItem('admin_email_backup');
      toast.success('Вы вернулись в админку');
      window.location.href = `/${currentLang}/admin/clients`;
    }
  };

  const SORT_OPTIONS = [
    { id: 'popular', name: t('catalog.sort.popular') },
    { id: 'price_asc', name: t('catalog.sort.priceAsc') },
    { id: 'price_desc', name: t('catalog.sort.priceDesc') },
    { id: 'name_asc', name: t('catalog.sort.nameAsc') },
    { id: 'new', name: t('catalog.sort.new') },
  ];
  
  // Products & Cart
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [units, setUnits] = useState([]);
  const [cart, setCart] = useState(() => {
    const saved = localStorage.getItem('cart');
    return saved ? JSON.parse(saved) : [];
  });
  const [loading, setLoading] = useState(true);
  // True only after all cold-start retries are exhausted and we still have no products.
  const [loadFailed, setLoadFailed] = useState(false);
  
  // Favorites & frequently ordered
  const [favoriteIds, setFavoriteIds] = useState(new Set());
  const [orderCounts, setOrderCounts] = useState({});
  const [showFavorites, setShowFavorites] = useState(false);
  
  // City state
  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState(() => {
    return localStorage.getItem('selectedCity') || 'almaty';
  });
  const [cityInfo, setCityInfo] = useState(null);
  const [showCityModal, setShowCityModal] = useState(() => {
    // Show modal on first visit (no city selected yet)
    return !localStorage.getItem('selectedCity');
  });
  
  // Filters & Search
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('popular');
  const [priceRange, setPriceRange] = useState([0, 10000]);
  const [maxPrice, setMaxPrice] = useState(10000);
  
  // UI State
  const [cartOpen, setCartOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  
  // Quantity state for product cards (productId -> quantity)
  const [productQuantities, setProductQuantities] = useState({});

  // Product details modal
  const [detailProduct, setDetailProduct] = useState(null);
  
  // Variant grouping: selected variant per parent product
  const [selectedVariants, setSelectedVariants] = useState({});
  
  // Order state
  const [addresses, setAddresses] = useState([]);
  const [legalEntities, setLegalEntities] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState('');
  const [selectedLegalEntity, setSelectedLegalEntity] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [legalConsentAccepted, setLegalConsentAccepted] = useState(false);
  const [legalDocsVersions, setLegalDocsVersions] = useState({});
  const [termsAlreadyAccepted, setTermsAlreadyAccepted] = useState(false);
  const [deliveryTime, setDeliveryTime] = useState('');
  const [deliveryNotes, setDeliveryNotes] = useState('');
  const [ordering, setOrdering] = useState(false);
  // Synchronous lock to block React's async state lag during double-clicks (P0)
  const orderInFlightRef = useRef(false);
  // Generation guard: invalidates stale fetchProducts retry chains so a new
  // fetch (e.g. city change / route change) cancels the previous one and we
  // don't hammer a cold backend with parallel chains.
  const fetchGenRef = useRef(0);
  
  // Delivery fee state
  const [deliveryFeeInfo, setDeliveryFeeInfo] = useState(null);
  const [loadingDeliveryFee, setLoadingDeliveryFee] = useState(false);
  const [deliveryType, setDeliveryType] = useState('delivery'); // 'delivery' or 'pickup'
  
  // Quick add address/entity dialogs
  const [showAddAddressDialog, setShowAddAddressDialog] = useState(false);
  const [showAddEntityDialog, setShowAddEntityDialog] = useState(false);
  const [newAddress, setNewAddress] = useState({
    label: 'Дом',
    address_line: '',
    city: 'Алматы',
    district: '',
    phone: '',
    is_default: true
  });
  const [newEntity, setNewEntity] = useState({
    company_name: '',
    bin_iin: '',
    legal_address: '',
    is_default: true
  });

  // Generate delivery time slots
  const timeSlots = [
    '06:00', '07:00', '08:00', '09:00', '10:00', '11:00', '12:00', '13:00', 
    '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00', '21:00',
    '22:00', '23:00', '24:00'
  ];

  // Get minimum delivery date based on city settings
  const getMinDeliveryDate = () => {
    // Almaty (UTC+5) local date — avoid UTC shift giving yesterday before 05:00
    const almaty = new Date(Date.now() + (5 * 60 + new Date().getTimezoneOffset()) * 60000);
    // If delivery today is disabled for this city, start from tomorrow
    if (cityInfo && !cityInfo.delivery_today_enabled) {
      almaty.setDate(almaty.getDate() + 1);
    }
    const m = String(almaty.getMonth() + 1).padStart(2, '0');
    const d = String(almaty.getDate()).padStart(2, '0');
    return `${almaty.getFullYear()}-${m}-${d}`;
  };

  // Save cart to localStorage
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    fetchCities();
    fetchCategories();
    fetchUnits();
    if (user) {
      fetchAddresses();
      if (user.role === 'B2B') {
        fetchLegalEntities();
      }
      // Fetch legal docs versions + check if user already accepted terms (B2C only)
      if (user.role === 'B2C') {
        (async () => {
          try {
            const r = await axios.get(`${API}/legal`);
            const vmap = {};
            (r.data || []).forEach(d => { vmap[d.slug] = d.version; });
            setLegalDocsVersions(vmap);
          } catch { /* legal docs optional */ }
          if (user.terms_accepted_at) {
            setTermsAlreadyAccepted(true);
            setLegalConsentAccepted(true);
          }
        })();
      }
    }
  }, [user]);

  // Helper to get localized name
  const getLocalizedName = (item, field = 'name') => {
    if (!item) return '';
    return item[`${field}_${currentLang}`] || item[`${field}_ru`] || item[field] || '';
  };

  // Helper to get unit short name for display with proper localization
  const getUnitShortName = (unitValue) => {
    // Default fallback based on language
    const defaultUnits = {
      ru: 'за шт',
      en: 'per pcs',
      kz: 'дана'
    };
    const defaultUnit = defaultUnits[currentLang] || defaultUnits.ru;
    
    if (!unitValue) return defaultUnit;
    
    // Try to find by ID first
    let unit = units.find(u => u.id === unitValue);
    
    // If not found by ID, try to find by short name (in any language)
    if (!unit) {
      const lowerValue = unitValue.toLowerCase();
      unit = units.find(u => 
        (u.short_name_ru && u.short_name_ru.toLowerCase() === lowerValue) ||
        (u.short_name_en && u.short_name_en.toLowerCase() === lowerValue) ||
        (u.short_name_kz && u.short_name_kz.toLowerCase() === lowerValue) ||
        (u.short_name && u.short_name.toLowerCase() === lowerValue)
      );
    }
    
    if (unit) {
      // Use display field if available (already formatted correctly)
      const displayField = `display_${currentLang}`;
      if (unit[displayField]) {
        return unit[displayField];
      }
      
      // Fallback to short name with prefix
      const shortName = unit[`short_name_${currentLang}`] || unit.short_name_ru || unit.short_name || '';
      if (!shortName) return defaultUnit;
      
      // Format based on language
      if (currentLang === 'en') {
        return `per ${shortName}`;
      } else if (currentLang === 'kz') {
        return shortName;
      } else {
        return `за ${shortName}`;
      }
    }
    
    // If still not found, format raw value based on language
    if (currentLang === 'en') {
      return `per ${unitValue}`;
    } else if (currentLang === 'kz') {
      return unitValue;
    } else {
      return `за ${unitValue}`;
    }
  };
  
  // Helper to get localized product name
  const getProductName = (product) => {
    if (!product) return '';
    return product[`name_${currentLang}`] || product.name_ru || product.name || '';
  };
  
  // Helper to get localized product description
  const getProductDescription = (product) => {
    if (!product) return '';
    return product[`description_${currentLang}`] || product.description_ru || product.description || '';
  };

  // Get category display name
  const getCategoryDisplayName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat ? getLocalizedName(cat, 'name') : categoryId;
  };

  // Build CATEGORIES array dynamically
  const CATEGORIES = [
    { id: 'all', name: t('catalog.allProducts') },
    ...categories.map(c => ({ id: c.id, name: getLocalizedName(c, 'name') }))
  ];

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/settings/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to load categories');
    }
  };

  const fetchUnits = async () => {
    try {
      const response = await axios.get(`${API}/settings/units`);
      setUnits(response.data);
    } catch (error) {
      console.error('Failed to load units');
    }
  };

  const fetchCities = async () => {
    try {
      const response = await axios.get(`${API}/cities/public/list`);
      setCities(response.data);
    } catch (error) {
      console.error('Failed to load cities');
    }
  };

  const fetchProducts = async (retryAttempt = 0, gen = null) => {
    // Keep the loading screen up across ALL retries (only flip it at terminal states).
    // This makes a slow backend cold-start invisible to users instead of flashing
    // a false "Товары не найдены" between the quick retries.
    const MAX_PRODUCT_RETRIES = 6;
    if (retryAttempt === 0) {
      gen = ++fetchGenRef.current;
      setLoading(true);
      setLoadFailed(false);
    }
    // Abort stale chains: a newer fetch (city/route change) has superseded this one.
    if (gen !== fetchGenRef.current) return;
    // Backoff: 2s, 4s, 6s, 8s, 8s, 8s → ~36s total window, enough for a sleeping
    // container to wake up + warm the DB pool. Each attempt itself waits up to 20s.
    const scheduleRetry = () => {
      const delay = Math.min(2000 * (retryAttempt + 1), 8000);
      setTimeout(() => fetchProducts(retryAttempt + 1, gen), delay);
    };
    try {
      // Use city-specific prices
      const endpoint = `${API}/cities/public/products?city_code=${selectedCity}`;
      const response = await axios.get(endpoint, { timeout: 20000 });
      // A newer fetch chain superseded us while awaiting — drop this response.
      if (gen !== fetchGenRef.current) return;
      
      const cityData = response.data;
      setCityInfo(cityData.city);
      
      const activeProducts = cityData.products.filter(p => p.is_active !== false);

      // STABILITY GUARD: empty result during deploy/pool-restart is almost always
      // transient. Retry (keeping the loading screen) until the backend warms up.
      if (activeProducts.length === 0) {
        if (retryAttempt < MAX_PRODUCT_RETRIES) {
          scheduleRetry();
          return;
        }
        // Genuinely empty after all retries — only surface this if we never had products.
        if (!products || products.length === 0) setLoadFailed(true);
        setLoading(false);
        return;
      }
      
      // Apply discounts if user is B2B or belongs to a client group
      if (user?.role === 'B2B' || user?.client_group_id) {
        try {
          const pricesRes = await axios.get(`${API}/products/prices`, { headers: getAuthHeaders() });
          const pricesMap = {};
          pricesRes.data.forEach(p => {
            pricesMap[p.id] = { final_price: p.final_price, discount_applied: p.discount_applied, discount_type: p.discount_type };
          });
          
          activeProducts.forEach(p => {
            const serverPrice = pricesMap[p.id];
            if (serverPrice && serverPrice.discount_applied) {
              // Use server-calculated price (handles group, custom, and percentage discounts)
              p.final_price = serverPrice.final_price;
              p.discount_applied = true;
              p.discount_type = serverPrice.discount_type;
              p.retail_price = p.city_price;
            } else {
              p.final_price = p.city_price;
              p.retail_price = p.city_price;
            }
          });
        } catch (e) {
          // Fallback to city prices
          activeProducts.forEach(p => {
            p.final_price = p.city_price;
            p.retail_price = p.city_price;
          });
        }
      } else {
        // Use city prices for B2C
        activeProducts.forEach(p => {
          p.final_price = p.city_price;
          p.retail_price = p.city_price;
        });
      }
      
      setProducts(activeProducts);
      setLoadFailed(false);
      
      // Calculate max price for filter
      if (activeProducts.length > 0) {
        const max = Math.max(...activeProducts.map(p => p.final_price || p.retail_price));
        setMaxPrice(Math.ceil(max / 100) * 100);
        setPriceRange([0, Math.ceil(max / 100) * 100]);
      }
      setLoading(false);
    } catch (error) {
      // STABILITY GUARD: on network error / cold start, KEEP retrying with the
      // loading screen up rather than nuking the catalog.
      if (retryAttempt < MAX_PRODUCT_RETRIES) {
        scheduleRetry();
        return;
      }
      // Fallback to old endpoint (last resort after retries exhausted)
      try {
        const endpoint = user?.role === 'B2B' 
          ? `${API}/products/prices` 
          : `${API}/products`;
        const headers = user ? getAuthHeaders() : {};
        const response = await axios.get(endpoint, { headers, timeout: 20000 });
        
        const activeProducts = response.data.filter(p => p.is_active !== false);
        if (activeProducts.length > 0) {
          setProducts(activeProducts);
          setLoadFailed(false);
          const max = Math.max(...activeProducts.map(p => p.final_price || p.retail_price));
          setMaxPrice(Math.ceil(max / 100) * 100);
          setPriceRange([0, Math.ceil(max / 100) * 100]);
        } else if (!products || products.length === 0) {
          setLoadFailed(true);
        }
      } catch (e) {
        // Only flag failure if we have NO products on screen at all
        if (!products || products.length === 0) {
          setLoadFailed(true);
        }
      }
      setLoading(false);
    }
  };

  // Handle city change
  const handleCityChange = (cityCode, fromModal = false) => {
    setSelectedCity(cityCode);
    localStorage.setItem('selectedCity', cityCode);
    if (fromModal) {
      setShowCityModal(false);
    }
  };

  // Fetch favorites & order frequency for logged-in users
  const fetchFavorites = async () => {
    if (!user) return;
    try {
      const results = await Promise.allSettled([
        axios.get(`${API}/favorites`, { headers: getAuthHeaders() }),
        axios.get(`${API}/favorites/frequently-ordered`, { headers: getAuthHeaders() })
      ]);
      if (results[0].status === 'fulfilled') setFavoriteIds(new Set(results[0].value.data.product_ids));
      if (results[1].status === 'fulfilled') setOrderCounts(results[1].value.data.product_order_counts || {});
    } catch (e) { console.error('Failed to load favorites'); }
  };

  const toggleFavorite = async (productId, e) => {
    e?.stopPropagation();
    if (!user) { toast.error('Войдите в аккаунт'); return; }
    try {
      const res = await axios.post(`${API}/favorites/${productId}`, {}, { headers: getAuthHeaders() });
      setFavoriteIds(prev => {
        const next = new Set(prev);
        if (res.data.is_favorite) next.add(productId);
        else next.delete(productId);
        return next;
      });
    } catch (e) { toast.error('Ошибка'); }
  };

  // Refetch products when city changes
  useEffect(() => {
    fetchProducts();
    fetchFavorites();
  }, [selectedCity, location.pathname]);

  const fetchAddresses = async () => {
    try {
      const response = await axios.get(`${API}/addresses`, { headers: getAuthHeaders() });
      setAddresses(response.data);
      // Auto-select delivery address first, then default
      const deliveryAddr = response.data.find(addr => addr.is_delivery);
      const defaultAddr = response.data.find(addr => addr.is_default);
      if (deliveryAddr) {
        setSelectedAddress(deliveryAddr.id);
      } else if (defaultAddr) {
        setSelectedAddress(defaultAddr.id);
      }
    } catch (error) {
      console.error('Failed to load addresses');
    }
  };

  const fetchLegalEntities = async () => {
    try {
      const response = await axios.get(`${API}/legal-entities`, { headers: getAuthHeaders() });
      setLegalEntities(response.data);
      const defaultEntity = response.data.find(e => e.is_default);
      if (defaultEntity) {
        setSelectedLegalEntity(defaultEntity.id);
      }
    } catch (error) {
      console.error('Failed to load legal entities');
    }
  };

  const handleQuickAddAddress = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/addresses`, newAddress, { headers: getAuthHeaders() });
      setAddresses([...addresses, response.data]);
      setSelectedAddress(response.data.id);
      setShowAddAddressDialog(false);
      setNewAddress({ label: 'Дом', address_line: '', city: 'Алматы', district: '', phone: '', is_default: true });
      toast.success(t('addresses.added'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('addresses.addError') || 'Не удалось добавить адрес');
    }
  };

  const handleQuickAddEntity = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/legal-entities`, newEntity, { headers: getAuthHeaders() });
      setLegalEntities([...legalEntities, response.data]);
      setSelectedLegalEntity(response.data.id);
      setShowAddEntityDialog(false);
      setNewEntity({ company_name: '', bin_iin: '', legal_address: '', is_default: true });
      toast.success(t('legalEntities.added'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('legalEntities.addError') || 'Не удалось добавить юр. лицо');
    }
  };

  // Filtered & Sorted Products
  const filteredProducts = useMemo(() => {
    let result = [...products];
    
    // Search - across all language fields
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(p => {
        // Search in all name fields (current language, RU, EN, KZ)
        const nameMatch = 
          (p.name && p.name.toLowerCase().includes(query)) ||
          (p.name_ru && p.name_ru.toLowerCase().includes(query)) ||
          (p.name_en && p.name_en.toLowerCase().includes(query)) ||
          (p.name_kz && p.name_kz.toLowerCase().includes(query));
        
        // Search in all description fields
        const descMatch = 
          (p.description && p.description.toLowerCase().includes(query)) ||
          (p.description_ru && p.description_ru.toLowerCase().includes(query)) ||
          (p.description_en && p.description_en.toLowerCase().includes(query)) ||
          (p.description_kz && p.description_kz.toLowerCase().includes(query));
        
        // Search in SKU
        const skuMatch = p.sku && p.sku.toLowerCase().includes(query);
        
        return nameMatch || descMatch || skuMatch;
      });
    }
    
    // Category
    if (selectedCategory !== 'all') {
      result = result.filter(p => p.category === selectedCategory);
    }
    
    // Price range
    result = result.filter(p => {
      const price = p.final_price || p.retail_price;
      return price >= priceRange[0] && price <= priceRange[1];
    });
    
    // Sort
    switch (sortBy) {
      case 'price_asc':
        result.sort((a, b) => (a.final_price || a.retail_price) - (b.final_price || b.retail_price));
        break;
      case 'price_desc':
        result.sort((a, b) => (b.final_price || b.retail_price) - (a.final_price || a.retail_price));
        break;
      case 'name_asc':
        result.sort((a, b) => {
          const nameA = a[`name_${currentLang}`] || a.name_ru || a.name || '';
          const nameB = b[`name_${currentLang}`] || b.name_ru || b.name || '';
          return nameA.localeCompare(nameB);
        });
        break;
      case 'new':
        result.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
        break;
      default:
        // popular — PRESERVED smart-sort: clients' frequently ordered items stay on top.
        // For products NOT ordered yet (count=0) AND for guests — fall back to category.sort_order.
        // RACE-CONDITION GUARD: if categories haven't loaded yet, KEEP backend order
        // (which is already sorted by category.sort_order → product.sort_order → name).
        // Re-sorting with an empty catSortMap would degrade to alphabetic and cause
        // a visible "flicker" once categories arrive.
        if (categories.length === 0 && Object.keys(orderCounts).length === 0) {
          break;
        }
        // Build lookup: categoryId -> sort_order
        const catSortMap = {};
        for (const c of categories) {
          catSortMap[c.id] = c.sort_order ?? 9999;
        }
        const getCatSort = (p) => catSortMap[p.category || p.category_id] ?? 9999;
        // Build variant children map to sum orders for grouped products (for both branches)
        const childrenOf = {};
        for (const p of result) {
          if (p.parent_product_id) {
            if (!childrenOf[p.parent_product_id]) childrenOf[p.parent_product_id] = [];
            childrenOf[p.parent_product_id].push(p);
          }
        }
        const countFor = (p) => (orderCounts[p.id] || 0) + (childrenOf[p.id] || []).reduce((s, c) => s + (orderCounts[c.id] || 0), 0);
        
        if (user && Object.keys(orderCounts).length > 0) {
          // Logged-in client with order history:
          // 1. Items ordered most → top (smart sort, EXISTING behavior)
          // 2. Items never ordered → grouped by category sort_order, then by name
          result.sort((a, b) => {
            const aCount = countFor(a);
            const bCount = countFor(b);
            if (aCount !== bCount) return bCount - aCount;
            // Both same count (likely both 0) → fall back to category order
            const catDiff = getCatSort(a) - getCatSort(b);
            if (catDiff !== 0) return catDiff;
            // Same category → by name
            const nameA = a[`name_${currentLang}`] || a.name_ru || a.name || '';
            const nameB = b[`name_${currentLang}`] || b.name_ru || b.name || '';
            return nameA.localeCompare(nameB);
          });
        } else {
          // Guest or client without history: pure category order
          result.sort((a, b) => {
            const catDiff = getCatSort(a) - getCatSort(b);
            if (catDiff !== 0) return catDiff;
            const nameA = a[`name_${currentLang}`] || a.name_ru || a.name || '';
            const nameB = b[`name_${currentLang}`] || b.name_ru || b.name || '';
            return nameA.localeCompare(nameB);
          });
        }
        break;
    }
    
    return result;
  }, [products, searchQuery, selectedCategory, sortBy, priceRange, orderCounts, user, categories, currentLang]);

  // Group variants: products with parent_product_id are hidden, shown under parent card.
  // PROMOTE: if a parent is inactive (not in filteredProducts), the first active variant
  // is promoted to display position so the group is still visible to customers.
  const { displayProducts, variantMap } = useMemo(() => {
    // Build map: parentId -> sorted list of child products
    const childrenMap = {};
    for (const p of filteredProducts) {
      if (p.parent_product_id) {
        if (!childrenMap[p.parent_product_id]) childrenMap[p.parent_product_id] = [];
        childrenMap[p.parent_product_id].push(p);
      }
    }
    // Sort children by variant_sort
    for (const key of Object.keys(childrenMap)) {
      childrenMap[key].sort((a, b) => (a.variant_sort || 0) - (b.variant_sort || 0));
    }
    // Set of products that ARE shown as cards (parents + standalone)
    const shownAsCard = new Set(filteredProducts.filter(p => !p.parent_product_id).map(p => p.id));
    // Build display list, promoting orphan variants whose parent is inactive
    const display = [];
    const promotedVariantMap = { ...childrenMap };
    for (const p of filteredProducts) {
      if (!p.parent_product_id) {
        display.push(p);
      }
    }
    // Find orphan groups and promote
    const handledParents = new Set();
    for (const parentId of Object.keys(childrenMap)) {
      if (handledParents.has(parentId)) continue;
      if (!shownAsCard.has(parentId)) {
        const variants = childrenMap[parentId];
        if (variants && variants.length > 0) {
          const promoted = variants[0];
          display.push(promoted);
          const siblings = variants.slice(1);
          if (siblings.length > 0) promotedVariantMap[promoted.id] = siblings;
          delete promotedVariantMap[parentId];
        }
        handledParents.add(parentId);
      }
    }
    // RE-SORT display after promotion so promoted variants land in their correct category position.
    // Preserves smart-sort: items with order history still come first.
    // RACE-CONDITION GUARD: if categories aren't loaded yet, keep backend order to avoid flicker.
    if (sortBy === 'popular' && categories.length === 0 && Object.keys(orderCounts).length === 0) {
      return { displayProducts: display, variantMap: promotedVariantMap };
    }
    const catSortMap = {};
    for (const c of categories) catSortMap[c.id] = c.sort_order ?? 9999;
    const getCatSort = (p) => catSortMap[p.category || p.category_id] ?? 9999;
    const countFor = (p) => (orderCounts[p.id] || 0) + (childrenMap[p.id] || []).reduce((s, c) => s + (orderCounts[c.id] || 0), 0);
    if (sortBy === 'popular') {
      display.sort((a, b) => {
        const ac = countFor(a), bc = countFor(b);
        if (ac !== bc) return bc - ac;
        const cd = getCatSort(a) - getCatSort(b);
        if (cd !== 0) return cd;
        const nameA = a[`name_${currentLang}`] || a.name_ru || a.name || '';
        const nameB = b[`name_${currentLang}`] || b.name_ru || b.name || '';
        return nameA.localeCompare(nameB);
      });
    }
    return { displayProducts: display, variantMap: promotedVariantMap };
  }, [filteredProducts, categories, orderCounts, sortBy, currentLang]);

  // Get current active product for a card (selected variant or parent itself)
  const getActiveProduct = (product) => {
    const variants = variantMap[product.id];
    if (!variants || variants.length === 0) return product;
    const selectedId = selectedVariants[product.id];
    if (selectedId) {
      const found = variants.find(v => v.id === selectedId);
      if (found) return found;
    }
    // Default: the parent itself (which is also a valid product)
    return product;
  };

  // Favorite products list
  const favoriteProducts = useMemo(() => {
    if (favoriteIds.size === 0) return [];
    return products.filter(p => favoriteIds.has(p.id));
  }, [products, favoriteIds]);

  const addToCart = (product, quantity = 1) => {
    const price = product.final_price || product.retail_price;
    const qty = Math.max(1, parseInt(quantity) || 1);
    const existing = cart.find(item => item.product_id === product.id);
    const productName = getProductName(product);
    
    if (existing) {
      setCart(cart.map(item =>
        item.product_id === product.id
          ? { ...item, quantity: item.quantity + qty }
          : item
      ));
    } else {
      setCart([...cart, {
        product_id: product.id,
        product_name: productName,
        quantity: qty,
        unit_price: price,
        image_url: product.image_url,
      }]);
    }
    // Reset quantity for this product after adding
    setProductQuantities(prev => ({ ...prev, [product.id]: 1 }));
    toast.success(`${productName} ${t('catalog.addedToCart')}`);
  };

  const updateQuantity = (productId, delta) => {
    setCart(cart.map(item => {
      if (item.product_id === productId) {
        const newQty = item.quantity + delta;
        return newQty > 0 ? { ...item, quantity: newQty } : item;
      }
      return item;
    }).filter(item => item.quantity > 0));
  };

  // Set exact quantity for cart item
  const setCartItemQuantity = (productId, newQuantity) => {
    const qty = Math.max(1, parseInt(newQuantity) || 1);
    setCart(cart.map(item =>
      item.product_id === productId
        ? { ...item, quantity: qty }
        : item
    ));
  };

  // Get/set quantity for product card
  const getProductQuantity = (productId) => productQuantities[productId] || 1;
  
  const setProductQuantity = (productId, quantity) => {
    const qty = Math.max(1, parseInt(quantity) || 1);
    setProductQuantities(prev => ({ ...prev, [productId]: qty }));
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.product_id !== productId));
  };

  const cartTotal = cart.reduce((sum, item) => sum + item.unit_price * item.quantity, 0);
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  // Calculate delivery fee when cart total or selected address changes
  useEffect(() => {
    const calculateDeliveryFee = async () => {
      // Skip if pickup selected
      if (deliveryType === 'pickup') {
        setDeliveryFeeInfo({ delivery_available: true, delivery_fee: 0, is_free: true, reason: 'Самовывоз' });
        return;
      }
      
      // Get city ID
      const city = cities.find(c => c.code === selectedCity);
      if (!city) return;
      
      // Get address info for geocoding
      let district = null;
      let fullAddress = null;
      if (selectedAddress) {
        const addr = addresses.find(a => a.id === selectedAddress);
        if (addr) {
          district = addr.district || null;
          // Build full address for geocoding if district not available
          if (!district && addr.address_line) {
            fullAddress = `${addr.city || selectedCity}, ${addr.address_line}`;
          }
        }
      }
      
      setLoadingDeliveryFee(true);
      try {
        const params = new URLSearchParams({
          order_amount: cartTotal.toString(),
          city_id: city.id
        });
        if (district) {
          params.append('district', district);
        } else if (fullAddress) {
          params.append('address', fullAddress);
        }
        
        const response = await axios.post(`${API}/delivery/calculate-fee?${params.toString()}`);
        setDeliveryFeeInfo(response.data);
      } catch (error) {
        console.error('Error calculating delivery fee:', error);
        // Set default values on error
        setDeliveryFeeInfo({
          delivery_available: true,
          delivery_fee: 0,
          is_free: false,
          reason: 'Не удалось рассчитать доставку'
        });
      } finally {
        setLoadingDeliveryFee(false);
      }
    };
    
    if (cartTotal > 0) {
      calculateDeliveryFee();
    } else {
      setDeliveryFeeInfo(null);
    }
  }, [cartTotal, selectedCity, selectedAddress, deliveryType, cities, addresses]);

  // Final total including delivery
  const finalTotal = cartTotal + (deliveryFeeInfo?.delivery_fee || 0);

  const placeOrder = async () => {
    // P0: synchronous lock — blocks the 2nd click before React processes setOrdering(true)
    if (orderInFlightRef.current) {
      return;
    }
    orderInFlightRef.current = true;
    // Check if user is logged in
    if (!user) {
      orderInFlightRef.current = false;
      toast.error(t('cart.loginRequired'));
      localStorage.setItem('cart', JSON.stringify(cart)); // Save cart
      navigate(`/${currentLang}/login`);
      return;
    }

    if (cart.length === 0) {
      orderInFlightRef.current = false;
      toast.error(t('cart.empty'));
      return;
    }

    // For delivery orders, validate address and time
    if (deliveryType === 'delivery') {
      // Validate delivery address
      if (!selectedAddress) {
        orderInFlightRef.current = false;
        toast.error(t('cart.addressRequired') || 'Добавьте адрес доставки');
        return;
      }

      // Validate delivery date and time
      if (!deliveryDate || !deliveryTime) {
        orderInFlightRef.current = false;
        toast.error(t('cart.selectDeliveryDateTime') || 'Выберите дату и время доставки');
        return;
      }
      
      // Check minimum order for delivery
      if (deliveryFeeInfo && !deliveryFeeInfo.delivery_available) {
        orderInFlightRef.current = false;
        toast.error(deliveryFeeInfo.reason || 'Доставка недоступна');
        return;
      }

      // Block if delivery fee hasn't been calculated yet
      if (loadingDeliveryFee || !deliveryFeeInfo) {
        orderInFlightRef.current = false;
        toast.error('Подождите, идёт расчёт стоимости доставки');
        return;
      }
    }

    // For pickup orders, validate date and time
    if (deliveryType === 'pickup') {
      if (!deliveryDate || !deliveryTime) {
        orderInFlightRef.current = false;
        toast.error('Выберите дату и время самовывоза');
        return;
      }
    }

    // Block past delivery dates (min= attribute can be bypassed by typing)
    if (deliveryDate && deliveryDate < getMinDeliveryDate()) {
      orderInFlightRef.current = false;
      toast.error('Нельзя выбрать прошедшую дату — укажите актуальную дату');
      return;
    }

    // Check if online orders are enabled for this city
    if (cityInfo && !cityInfo.online_orders_enabled) {
      orderInFlightRef.current = false;
      toast.error(cityInfo.offline_message || 'Онлайн-заказы недоступны в этом городе');
      return;
    }

    setOrdering(true);
    try {
      const orderData = {
        items: cart.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity
        })),
        address_id: deliveryType === 'delivery' ? selectedAddress : null,
        legal_entity_id: user.role === 'B2B' ? (selectedLegalEntity || null) : null,
        delivery_date: deliveryDate || null,
        delivery_time: deliveryTime || null,
        delivery_notes: deliveryNotes || null,
        city: selectedCity,
        city_id: cities.find(c => c.code === selectedCity)?.id || null,
        delivery_type: deliveryType,
        delivery_fee: deliveryType === 'delivery' ? (deliveryFeeInfo?.delivery_fee || 0) : 0,
        delivery_distance_km: deliveryType === 'delivery' ? (deliveryFeeInfo?.distance_km || null) : null,
        legal_consent_accepted: legalConsentAccepted || undefined,
        legal_consent_versions: legalConsentAccepted ? legalDocsVersions : undefined,
      };

      await axios.post(`${API}/orders`, orderData, { headers: getAuthHeaders() });
      toast.success(t('cart.orderSuccess'));
      setCart([]);
      localStorage.removeItem('cart');
      setCartOpen(false);
      // Reset delivery fields
      setDeliveryDate('');
      setDeliveryTime('');
      setDeliveryNotes('');
      setDeliveryType('delivery');
      navigate(`/${currentLang}/orders`);
    } catch (error) {
      // B2C must prepay: backend returns 402 with payment_url (web redirect) OR
      // just invoice_id (Kaspi sends push to client's phone — no redirect needed)
      const detail = error.response?.data?.detail;
      if (error.response?.status === 402 && detail?.code === 'PAYMENT_REQUIRED' && detail?.invoice_id) {
        sessionStorage.setItem('pending_invoice_id', detail.invoice_id);
        if (detail.payment_url) {
          toast.info('Перенаправляем на оплату через Kaspi...');
          window.location.href = detail.payment_url;
        } else {
          toast.info('Откройте Kaspi на телефоне — пришло уведомление об оплате');
          navigate(`/${currentLang}/payment/success?invoice_id=${detail.invoice_id}&amount=${detail.amount || ''}`);
        }
        return;
      }
      if (error.response?.status === 429 || detail?.code === 'DUPLICATE_ORDER') {
        toast.success(t('cart.orderSuccess') || 'Заказ уже оформлен!');
        setCart([]);
        localStorage.removeItem('cart');
        setCartOpen(false);
        navigate(`/${currentLang}/orders`);
        return;
      }
      if (detail?.code === 'INSUFFICIENT_STOCK') {
        toast.error(t('cart.insufficientStock', { product: detail.product_name, available: detail.available }));
      } else if (detail?.code === 'PRODUCT_NOT_FOUND') {
        toast.error(t('cart.productNotFound'));
      } else if (detail?.code === 'PRODUCT_NOT_AVAILABLE') {
        toast.error(t('cart.productNotAvailable', { product: detail.product_name }));
      } else if (detail?.code === 'DAILY_LIMIT_EXCEEDED') {
        toast.error(t('cart.dailyLimitExceeded', { product: detail.product_name, available: detail.available, limit: detail.daily_limit }));
      } else {
        toast.error(typeof detail === 'string' ? detail : t('cart.orderError'));
      }
    } finally {
      setOrdering(false);
      orderInFlightRef.current = false;
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('ru-RU').format(price) + ' ' + t('common.currency');
  };

  const getProductPrice = (product) => {
    return product.final_price || product.retail_price;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-xl text-gray-600">{t('common.loading')}</div>
      </div>
    );
  }

  const seoMeta = pickMeta('home', currentLang);

  return (
    <div className="min-h-screen bg-gray-50">
      <Seo lang={currentLang} path="" title={seoMeta.title} description={seoMeta.description} />
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-3 sm:px-4">
          <div className="flex items-center justify-between h-14 sm:h-16">
            {/* Logo */}
            <Link to={`/${currentLang}`} className="flex items-center flex-shrink-0">
              <img 
                src="/logo.jpg" 
                alt="Ainanan" 
                className="h-8 sm:h-10 object-contain"
              />
            </Link>

            {/* Search - Desktop */}
            <div className="hidden md:flex flex-1 max-w-xl mx-8">
              <div className="relative w-full">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="text"
                  placeholder={t('common.search')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 h-10 bg-gray-100 border-0 focus:bg-white focus:ring-2 focus:ring-stone-600"
                />
              </div>
            </div>

            {/* Navigation - Desktop */}
            <nav className="hidden md:flex items-center gap-6">
              <Link to={`/${currentLang}`} className="text-sm font-medium text-gray-900 hover:text-stone-700">
                {t('common.catalog')}
              </Link>
              <Link to={`/${currentLang}/about`} className="text-sm font-medium text-gray-600 hover:text-stone-700">
                {t('common.about')}
              </Link>
              <Link to={`/${currentLang}/contacts`} className="text-sm font-medium text-gray-600 hover:text-stone-700">
                {t('common.contacts')}
              </Link>
            </nav>

            {/* Right Section */}
            <div className="flex items-center gap-2">
              {/* Return to Admin Button */}
              {isAdminViewingAsClient && (
                <Button
                  onClick={handleReturnToAdmin}
                  size="sm"
                  className="bg-amber-500 hover:bg-amber-600 text-white hidden md:flex"
                  data-testid="return-to-admin-btn"
                >
                  <Shield className="w-4 h-4 mr-2" />
                  Вернуться в админку
                </Button>
              )}

              {/* City Selector */}
              {cities.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="hidden md:flex">
                      <MapPin className="w-4 h-4 mr-1" />
                      {cities.find(c => c.code === selectedCity)?.name || 'Город'}
                      <ChevronDown className="w-3 h-3 ml-1" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {cities.map(city => (
                      <DropdownMenuItem 
                        key={city.id} 
                        onClick={() => handleCityChange(city.code)}
                        className={selectedCity === city.code ? 'bg-amber-50' : ''}
                      >
                        <MapPin className="w-4 h-4 mr-2" />
                        {city.name}
                        {!city.online_orders_enabled && (
                          <span className="ml-2 text-xs text-orange-600">(по телефону)</span>
                        )}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              {/* Language Switcher */}
              <LanguageSwitcher />

              {/* Notification Bell */}
              {user && user.role !== 'Admin' && <NotificationBell user={user} />}

              {/* User Menu */}
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="hidden md:flex">
                      <User className="w-4 h-4 mr-2" />
                      {user.company_name || user.full_name || user.email.split('@')[0]}
                      <ChevronDown className="w-4 h-4 ml-1" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem onClick={() => navigate(`/${currentLang}/profile`)}>
                      <User className="w-4 h-4 mr-2" />
                      {t('common.myProfile') || 'Мой профиль'}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate(`/${currentLang}/orders`)}>
                      <Package className="w-4 h-4 mr-2" />
                      {t('common.myOrders')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate(`/${currentLang}/addresses`)}>
                      <MapPin className="w-4 h-4 mr-2" />
                      {t('common.myAddresses')}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate(`/${currentLang}/settings`)}>
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/><circle cx="12" cy="12" r="3"/></svg>
                      {t('common.settings') || 'Настройки'}
                    </DropdownMenuItem>
                    {['Admin', 'Manager', 'Support', 'Courier', 'ContentCreator'].includes(user.role) && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => navigate(`/${currentLang}/admin`)}>
                          {t('common.adminPanel')}
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={onLogout} className="text-red-600">
                      <LogOut className="w-4 h-4 mr-2" />
                      {t('common.logout')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(`/${currentLang}/login`)}
                  className="hidden md:flex"
                >
                  <User className="w-4 h-4 mr-2" />
                  {t('common.login')}
                </Button>
              )}

              {/* WhatsApp Order Button — visible on both desktop & mobile */}
              <a
                href={`https://wa.me/77010982754?text=${encodeURIComponent('Здравствуйте! Хочу оформить заказ через Айнанан')}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="whatsapp-order-btn"
                aria-label="Заказать через WhatsApp"
              >
                <Button
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5 px-2.5 sm:px-3"
                >
                  <svg viewBox="0 0 24 24" className="w-4 h-4 sm:w-[18px] sm:h-[18px]" fill="currentColor" aria-hidden="true">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.198-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/>
                  </svg>
                  <span className="hidden sm:inline text-xs sm:text-sm font-medium">Заказать</span>
                </Button>
              </a>

              {/* Cart Button */}
              <Sheet open={cartOpen} onOpenChange={setCartOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="sm" className="relative">
                    <ShoppingCart className="w-5 h-5" />
                    {cartCount > 0 && (
                      <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs bg-emerald-700">
                        {cartCount}
                      </Badge>
                    )}
                  </Button>
                </SheetTrigger>
                <SheetContent className="w-full sm:max-w-lg flex flex-col h-full" data-testid="cart-sheet">
                  <SheetHeader className="flex-shrink-0">
                    <SheetTitle>{t('cart.title')} ({cartCount})</SheetTitle>
                  </SheetHeader>
                  
                  {cart.length === 0 ? (
                    <div className="flex-1 flex items-center justify-center text-gray-500">
                      {t('cart.empty')}
                    </div>
                  ) : (
                    <>
                      {/* Scrollable area: items + delivery options */}
                      <div className="flex-1 overflow-y-auto min-h-0" data-testid="cart-scroll-area">
                        {/* Cart items */}
                        <div className="py-4 space-y-3 px-1">
                          {cart.map((item) => (
                            <div key={item.product_id} className="flex gap-3 p-3 bg-gray-50 rounded-lg" data-testid={`cart-item-${item.product_id}`}>
                              {item.image_url ? (
                                <img src={item.image_url} alt={item.product_name} className="w-14 h-14 object-cover rounded flex-shrink-0" />
                              ) : (
                                <div className="w-14 h-14 bg-gray-200 rounded flex items-center justify-center flex-shrink-0">
                                  <Package className="w-5 h-5 text-gray-400" />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <h4 className="font-medium text-sm truncate">{item.product_name}</h4>
                                <p className="text-stone-700 font-bold text-sm">{formatPrice(item.unit_price)}</p>
                                <div className="flex items-center gap-2 mt-1">
                                  <Button
                                    variant="outline"
                                    size="icon"
                                    className="h-7 w-7"
                                    onClick={() => updateQuantity(item.product_id, -1)}
                                  >
                                    <Minus className="w-3 h-3" />
                                  </Button>
                                  <input
                                    type="text"
                                    inputMode="numeric"
                                    value={item.quantity}
                                    onChange={(e) => setCartItemQuantity(item.product_id, e.target.value)}
                                    className="w-10 h-7 text-center text-sm font-medium border rounded focus:outline-none focus:ring-1 focus:ring-emerald-500"
                                    data-testid={`cart-quantity-${item.product_id}`}
                                  />
                                  <Button
                                    variant="outline"
                                    size="icon"
                                    className="h-7 w-7"
                                    onClick={() => updateQuantity(item.product_id, 1)}
                                  >
                                    <Plus className="w-3 h-3" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="ml-auto text-red-500 hover:text-red-700 h-7 w-7 p-0"
                                    onClick={() => removeFromCart(item.product_id)}
                                  >
                                    <X className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Delivery options inside scrollable area */}
                        <div className="border-t px-1 pt-4 pb-2 space-y-4">
                          {/* City info - Online orders disabled warning */}
                          {cityInfo && !cityInfo.online_orders_enabled && (
                            <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                              <div className="flex items-center gap-2 text-orange-700 text-sm mb-2">
                                <AlertCircle className="w-4 h-4" />
                                {cityInfo.offline_message || 'Онлайн-заказы временно недоступны'}
                              </div>
                              {cityInfo.offline_contact_url && (
                                <a
                                  href={cityInfo.offline_contact_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-2 px-4 py-2 bg-green-500 text-white text-sm rounded-lg hover:bg-green-600"
                                >
                                  <Phone className="w-4 h-4" />
                                  {cityInfo.offline_contact_label || 'Связаться'}
                                </a>
                              )}
                            </div>
                          )}

                          {/* Address selection for logged in users */}
                          {user && cityInfo?.online_orders_enabled !== false && (
                            <div>
                              <label className="text-sm text-gray-600 mb-1 flex items-center gap-2">
                                <MapPin className="w-4 h-4" />
                                {t('cart.deliveryAddress')}
                              </label>
                              {addresses.length > 0 ? (
                                <Select value={selectedAddress} onValueChange={setSelectedAddress}>
                                  <SelectTrigger>
                                    <SelectValue placeholder={t('cart.selectAddress')} />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {addresses.map(addr => (
                                      <SelectItem key={addr.id} value={addr.id}>
                                        <div className="flex flex-col">
                                          <span className="font-medium">
                                            {addr.label}
                                            {addr.is_delivery && <span className="ml-2 text-xs bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">доставка</span>}
                                          </span>
                                          <span className="text-xs text-gray-500">{addr.city}, {addr.address_line}</span>
                                        </div>
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : (
                                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                  <div className="flex items-center gap-2 text-amber-700 text-sm mb-2">
                                    <AlertCircle className="w-4 h-4" />
                                    {t('addresses.empty')}
                                  </div>
                                  <Button 
                                    variant="outline" 
                                    size="sm" 
                                    className="w-full border-amber-300 text-amber-700 hover:bg-amber-100"
                                    onClick={() => setShowAddAddressDialog(true)}
                                  >
                                    <Plus className="w-4 h-4 mr-2" />
                                    {t('addresses.add')}
                                  </Button>
                                </div>
                              )}
                              {addresses.length > 0 && (
                                <Button 
                                  variant="link" 
                                  size="sm" 
                                  className="text-xs text-emerald-700 p-0 h-auto mt-1"
                                  onClick={() => setShowAddAddressDialog(true)}
                                >
                                  <Plus className="w-3 h-3 mr-1" />
                                  {t('addresses.addNew')}
                                </Button>
                              )}
                            </div>
                          )}

                          {/* Legal entity selection for B2B users */}
                          {user && user.role === 'B2B' && (
                            <div>
                              <label className="text-sm text-gray-600 mb-1 flex items-center gap-2">
                                <Building2 className="w-4 h-4" />
                                {t('legalEntities.title')}
                              </label>
                              {legalEntities.length > 0 ? (
                                <Select value={selectedLegalEntity} onValueChange={setSelectedLegalEntity}>
                                  <SelectTrigger>
                                    <SelectValue placeholder={t('legalEntities.title')} />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {legalEntities.map(entity => (
                                      <SelectItem key={entity.id} value={entity.id}>
                                        <div className="flex flex-col">
                                          <span className="font-medium">{entity.company_name}</span>
                                          <span className="text-xs text-gray-500">БИН: {entity.bin_iin}</span>
                                        </div>
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : (
                                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                  <div className="flex items-center gap-2 text-blue-700 text-sm mb-2">
                                    <AlertCircle className="w-4 h-4" />
                                    {t('legalEntities.empty')}
                                  </div>
                                  <Button 
                                    variant="outline" 
                                    size="sm" 
                                    className="w-full border-blue-300 text-blue-700 hover:bg-blue-100"
                                    onClick={() => setShowAddEntityDialog(true)}
                                  >
                                    <Plus className="w-4 h-4 mr-2" />
                                    {t('legalEntities.add')}
                                  </Button>
                                </div>
                              )}
                              {legalEntities.length > 0 && (
                                <Button 
                                  variant="link" 
                                  size="sm" 
                                  className="text-xs text-emerald-700 p-0 h-auto mt-1"
                                  onClick={() => setShowAddEntityDialog(true)}
                                >
                                  <Plus className="w-3 h-3 mr-1" />
                                  {t('legalEntities.addNew')}
                                </Button>
                              )}
                            </div>
                          )}

                          {/* Delivery Date & Time Selection */}
                          {user && (
                            <div className="border-t pt-4 space-y-3">
                              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="18" x="3" y="4" rx="2" ry="2"/><line x1="16" x2="16" y1="2" y2="6"/><line x1="8" x2="8" y1="2" y2="6"/><line x1="3" x2="21" y1="10" y2="10"/></svg>
                                {deliveryType === 'pickup' 
                                  ? 'Дата и время самовывоза' 
                                  : (t('cart.deliveryDateTime') || 'Дата и время доставки')}
                              </label>
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <Input
                                    type="date"
                                    value={deliveryDate}
                                    onChange={(e) => setDeliveryDate(e.target.value)}
                                    min={getMinDeliveryDate()}
                                    className="text-sm"
                                  />
                                </div>
                                <div>
                                  <Select value={deliveryTime} onValueChange={setDeliveryTime}>
                                    <SelectTrigger className="text-sm">
                                      <SelectValue placeholder={t('cart.selectTime') || 'Время'} />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {timeSlots.map(time => (
                                        <SelectItem key={time} value={time}>
                                          {time}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                              </div>
                              <Input
                                placeholder={t('cart.deliveryNotes') || 'Комментарий к доставке (необязательно)'}
                                value={deliveryNotes}
                                onChange={(e) => setDeliveryNotes(e.target.value)}
                                className="text-sm"
                              />
                            </div>
                          )}
                          
                          {/* Delivery Type Toggle */}
                          {user && (
                            <div className="flex gap-2">
                              <Button
                                type="button"
                                variant={deliveryType === 'delivery' ? 'default' : 'outline'}
                                className={`flex-1 text-sm ${deliveryType === 'delivery' ? 'bg-emerald-700 hover:bg-emerald-800' : ''}`}
                                onClick={() => setDeliveryType('delivery')}
                              >
                                <MapPin className="w-4 h-4 mr-1" />
                                Доставка
                              </Button>
                              <Button
                                type="button"
                                variant={deliveryType === 'pickup' ? 'default' : 'outline'}
                                className={`flex-1 text-sm ${deliveryType === 'pickup' ? 'bg-emerald-700 hover:bg-emerald-800' : ''}`}
                                onClick={() => setDeliveryType('pickup')}
                              >
                                <Package className="w-4 h-4 mr-1" />
                                Самовывоз
                              </Button>
                            </div>
                          )}

                          {/* Validation hints */}
                          {user && deliveryType === 'delivery' && (!selectedAddress || !deliveryDate || !deliveryTime) && (
                            <div className="p-2 bg-orange-50 border border-orange-200 rounded-lg">
                              <p className="text-xs font-medium text-orange-800 mb-1">{t('cart.fillRequired') || 'Заполните:'}</p>
                              <ul className="text-xs text-orange-700 space-y-0.5">
                                {!selectedAddress && <li className="flex items-center gap-1"><span className="w-1 h-1 bg-orange-500 rounded-full"></span>{t('cart.addAddressHint') || 'Адрес доставки'}</li>}
                                {!deliveryDate && <li className="flex items-center gap-1"><span className="w-1 h-1 bg-orange-500 rounded-full"></span>{t('cart.selectDateHint') || 'Дата доставки'}</li>}
                                {!deliveryTime && <li className="flex items-center gap-1"><span className="w-1 h-1 bg-orange-500 rounded-full"></span>{t('cart.selectTimeHint') || 'Время доставки'}</li>}
                              </ul>
                            </div>
                          )}
                          
                          {user && deliveryType === 'pickup' && (!deliveryDate || !deliveryTime) && (
                            <div className="p-2 bg-orange-50 border border-orange-200 rounded-lg">
                              <p className="text-xs font-medium text-orange-800 mb-1">Заполните:</p>
                              <ul className="text-xs text-orange-700 space-y-0.5">
                                {!deliveryDate && <li className="flex items-center gap-1"><span className="w-1 h-1 bg-orange-500 rounded-full"></span>Дата самовывоза</li>}
                                {!deliveryTime && <li className="flex items-center gap-1"><span className="w-1 h-1 bg-orange-500 rounded-full"></span>Время самовывоза</li>}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                      {/* END scrollable area */}
                      
                      {/* Pinned bottom: summary + order button */}
                      <div className="flex-shrink-0 border-t bg-white pt-3 pb-1 space-y-2" data-testid="cart-footer">
                        <div className="space-y-1">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Товары ({cartCount}):</span>
                            <span>{formatPrice(cartTotal)}</span>
                          </div>
                          
                          {user && deliveryType === 'delivery' && (
                            <>
                              {loadingDeliveryFee ? (
                                <div className="flex justify-between text-sm">
                                  <span className="text-gray-600">Доставка:</span>
                                  <span className="text-gray-400">Расчёт...</span>
                                </div>
                              ) : deliveryFeeInfo && (
                                <div className="flex justify-between text-sm">
                                  <span className="text-gray-600">Доставка:</span>
                                  {deliveryFeeInfo.is_free ? (
                                    <span className="text-emerald-600 font-medium">Бесплатно</span>
                                  ) : deliveryFeeInfo.delivery_available ? (
                                    <span>{formatPrice(deliveryFeeInfo.delivery_fee)}</span>
                                  ) : (
                                    <span className="text-red-500 text-xs">{deliveryFeeInfo.reason}</span>
                                  )}
                                </div>
                              )}
                              {deliveryFeeInfo && !deliveryFeeInfo.is_free && deliveryFeeInfo.delivery_available && deliveryFeeInfo.free_delivery_threshold > 0 && (
                                <p className="text-xs text-emerald-600" data-testid="delivery-free-hint">
                                  Бесплатно от {formatPrice(deliveryFeeInfo.free_delivery_threshold)}
                                </p>
                              )}
                            </>
                          )}
                          
                          {user && deliveryType === 'pickup' && (
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">Самовывоз:</span>
                              <span className="text-emerald-600 font-medium">Бесплатно</span>
                            </div>
                          )}
                        </div>
                        
                        <div className="flex justify-between items-center text-lg font-bold border-t pt-2">
                          <span>{t('cart.total')}:</span>
                          <span className="text-stone-700">
                            {formatPrice(deliveryType === 'pickup' ? cartTotal : finalTotal)}
                          </span>
                        </div>
                        
                        {user && user.role === 'B2C' && !termsAlreadyAccepted && (
                          <label
                            className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg cursor-pointer text-sm"
                            data-testid="legal-consent-label"
                          >
                            <input
                              type="checkbox"
                              checked={legalConsentAccepted}
                              onChange={(e) => setLegalConsentAccepted(e.target.checked)}
                              className="mt-0.5 shrink-0 w-4 h-4 rounded border-amber-400 text-emerald-600 focus:ring-emerald-500"
                              data-testid="legal-consent-checkbox"
                            />
                            <span className="text-gray-700 leading-snug">
                              Я согласен с{' '}
                              <a href={`/${currentLang}/legal/oferta`} target="_blank" rel="noopener noreferrer" className="text-emerald-700 underline">публичной офертой</a>,{' '}
                              <a href={`/${currentLang}/legal/privacy`} target="_blank" rel="noopener noreferrer" className="text-emerald-700 underline">политикой конфиденциальности</a>{' '}
                              и{' '}
                              <a href={`/${currentLang}/legal/delivery`} target="_blank" rel="noopener noreferrer" className="text-emerald-700 underline">правилами доставки</a>
                            </span>
                          </label>
                        )}

                        <Button
                          className="w-full bg-emerald-700 hover:bg-emerald-800"
                          size="lg"
                          onClick={placeOrder}
                          disabled={
                            ordering || 
                            loadingDeliveryFee ||
                            (user && deliveryType === 'delivery' && (!selectedAddress || !deliveryDate || !deliveryTime)) ||
                            (user && deliveryType === 'delivery' && !deliveryFeeInfo) ||
                            (user && deliveryType === 'delivery' && deliveryFeeInfo && !deliveryFeeInfo.delivery_available) ||
                            (user && deliveryType === 'pickup' && (!deliveryDate || !deliveryTime)) ||
                            (user && user.role === 'B2C' && !termsAlreadyAccepted && !legalConsentAccepted)
                          }
                          data-testid="checkout-btn"
                        >
                          {ordering ? (
                            <span className="flex items-center justify-center gap-2">
                              <Loader2 className="w-4 h-4 animate-spin" />
                              {t('cart.processing')}
                            </span>
                          ) : loadingDeliveryFee ? 'Расчёт доставки...' : user ? t('cart.checkout') : t('cart.loginAndCheckout')}
                        </Button>
                        
                        {!user && (
                          <p className="text-xs text-center text-gray-500">
                            {t('cart.loginRequired')}
                          </p>
                        )}
                      </div>
                    </>
                  )}
                </SheetContent>
              </Sheet>

              {/* Quick Add Address Dialog */}
              <Dialog open={showAddAddressDialog} onOpenChange={setShowAddAddressDialog}>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-emerald-600" />
                      {t('addresses.addNew')}
                    </DialogTitle>
                    <DialogDescription>{t('addresses.fillFields')}</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleQuickAddAddress} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>{t('addresses.label')}</Label>
                        <Input
                          value={newAddress.label}
                          onChange={(e) => setNewAddress({...newAddress, label: e.target.value})}
                          placeholder="Дом"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>{t('addresses.city')} *</Label>
                        <Input
                          value={newAddress.city}
                          onChange={(e) => setNewAddress({...newAddress, city: e.target.value})}
                          placeholder="Алматы"
                          required
                          className="mt-1"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>{t('addresses.addressLine')} *</Label>
                      <AddressAutocomplete
                        value={newAddress.address_line}
                        onChange={(value) => setNewAddress({...newAddress, address_line: value})}
                        onAddressSelect={(data) => {
                          setNewAddress({
                            ...newAddress, 
                            address_line: data.address,
                            district: data.district || ''
                          });
                        }}
                        cityCode={selectedCity}
                        placeholder="Начните вводить адрес..."
                        className="mt-1"
                      />
                      {newAddress.district && (
                        <p className="text-xs text-emerald-600 mt-1">
                          Район: {newAddress.district}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label>{t('addresses.phone')} *</Label>
                      <Input
                        value={newAddress.phone}
                        onChange={(e) => setNewAddress({...newAddress, phone: e.target.value})}
                        placeholder="+7 777 123 4567"
                        required
                        className="mt-1"
                      />
                    </div>
                    <div className="flex gap-2 pt-2">
                      <Button type="button" variant="outline" onClick={() => setShowAddAddressDialog(false)} className="flex-1">
                        {t('common.cancel')}
                      </Button>
                      <Button type="submit" className="flex-1 bg-emerald-700 hover:bg-emerald-800">
                        {t('addresses.add')}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>

              {/* Quick Add Legal Entity Dialog */}
              <Dialog open={showAddEntityDialog} onOpenChange={setShowAddEntityDialog}>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <Building2 className="w-5 h-5 text-emerald-600" />
                      {t('legalEntities.addNew')}
                    </DialogTitle>
                    <DialogDescription>{t('legalEntities.fillFields')}</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleQuickAddEntity} className="space-y-4">
                    <div>
                      <Label>{t('legalEntities.companyName')} *</Label>
                      <Input
                        value={newEntity.company_name}
                        onChange={(e) => setNewEntity({...newEntity, company_name: e.target.value})}
                        placeholder="ТОО «Компания»"
                        required
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>{t('legalEntities.binIin')} *</Label>
                      <Input
                        value={newEntity.bin_iin}
                        onChange={(e) => setNewEntity({...newEntity, bin_iin: e.target.value})}
                        placeholder="123456789012"
                        required
                        maxLength={12}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>{t('legalEntities.legalAddress')} *</Label>
                      <Input
                        value={newEntity.legal_address}
                        onChange={(e) => setNewEntity({...newEntity, legal_address: e.target.value})}
                        placeholder="г. Алматы, ул. Абая 1, офис 100"
                        required
                        className="mt-1"
                      />
                    </div>
                    <div className="flex gap-2 pt-2">
                      <Button type="button" variant="outline" onClick={() => setShowAddEntityDialog(false)} className="flex-1">
                        {t('common.cancel')}
                      </Button>
                      <Button type="submit" className="flex-1 bg-emerald-700 hover:bg-emerald-800">
                        {t('legalEntities.add')}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>

              {/* City Selection Modal - First Visit */}
              <Dialog open={showCityModal && cities.length > 0} onOpenChange={setShowCityModal}>
                <DialogContent className="sm:max-w-md" data-testid="city-selection-modal">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-xl">
                      <MapPin className="w-6 h-6 text-emerald-600" />
                      {t('cityModal.title') || 'Выберите ваш город'}
                    </DialogTitle>
                    <DialogDescription>
                      {t('cityModal.description') || 'Для отображения актуальных цен и условий доставки укажите ваш город'}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="grid gap-3 py-4">
                    {cities.map(city => (
                      <button
                        key={city.id}
                        onClick={() => handleCityChange(city.code, true)}
                        data-testid={`city-option-${city.code}`}
                        className={`flex items-center justify-between p-4 rounded-xl border-2 transition-all hover:border-emerald-500 hover:bg-emerald-50 ${
                          selectedCity === city.code 
                            ? 'border-emerald-500 bg-emerald-50' 
                            : 'border-gray-200'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            selectedCity === city.code 
                              ? 'bg-emerald-100 text-emerald-600' 
                              : 'bg-gray-100 text-gray-500'
                          }`}>
                            <MapPin className="w-5 h-5" />
                          </div>
                          <div className="text-left">
                            <div className="font-semibold text-gray-900">{city.name}</div>
                            {!city.online_orders_enabled && (
                              <div className="text-xs text-orange-600 flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {t('cityModal.phoneOnly') || 'Заказ по телефону'}
                              </div>
                            )}
                          </div>
                        </div>
                        {selectedCity === city.code && (
                          <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center">
                            <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                  <div className="text-center text-xs text-gray-500">
                    {t('cityModal.changeAnytime') || 'Вы можете изменить город в любое время'}
                  </div>
                </DialogContent>
              </Dialog>

              {/* Mobile Menu Button */}
              <Button
                variant="ghost"
                size="sm"
                className="md:hidden"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>
          </div>

          {/* Mobile Search - Compact */}
          <div className="md:hidden pb-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                type="text"
                placeholder={t('common.search')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 h-9 bg-gray-100 border-0 text-sm rounded-xl"
              />
            </div>
          </div>
        </div>

        {/* Mobile Menu - Slide from top */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t bg-white animate-in slide-in-from-top-2 duration-200">
            <nav className="flex flex-col p-3 space-y-1">
              {/* City Selector - Mobile */}
              {cities.length > 0 && (
                <div className="pb-2 mb-2 border-b">
                  <label className="text-xs text-gray-500 mb-1 block">{t('catalog.selectCity') || 'Выберите город'}</label>
                  <Select value={selectedCity} onValueChange={(value) => { handleCityChange(value); setMobileMenuOpen(false); }}>
                    <SelectTrigger className="w-full h-10">
                      <SelectValue>
                        <span className="flex items-center gap-2">
                          <MapPin className="w-4 h-4" />
                          {cities.find(c => c.code === selectedCity)?.name || 'Город'}
                        </span>
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {cities.map(city => (
                        <SelectItem key={city.id} value={city.code}>
                          <span className="flex items-center gap-2">
                            {city.name}
                            {!city.online_orders_enabled && (
                              <span className="text-xs text-orange-600">(по телефону)</span>
                            )}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Return to Admin - Mobile */}
              {isAdminViewingAsClient && (
                <button
                  onClick={() => { handleReturnToAdmin(); setMobileMenuOpen(false); }}
                  className="py-2.5 px-4 bg-amber-500 text-white rounded-xl font-medium flex items-center gap-2 active:scale-95 transition-transform"
                >
                  <Shield className="w-4 h-4" />
                  Вернуться в админку
                </button>
              )}
              
              {/* Navigation Links */}
              <div className="grid grid-cols-3 gap-2 py-2">
                <Link 
                  to={`/${currentLang}`} 
                  className="py-3 px-2 text-center text-sm font-medium text-gray-900 bg-gray-100 rounded-xl active:scale-95 transition-transform" 
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t('common.catalog')}
                </Link>
                <Link 
                  to={`/${currentLang}/about`} 
                  className="py-3 px-2 text-center text-sm text-gray-600 bg-gray-50 rounded-xl active:scale-95 transition-transform" 
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t('common.about')}
                </Link>
                <Link 
                  to={`/${currentLang}/contacts`} 
                  className="py-3 px-2 text-center text-sm text-gray-600 bg-gray-50 rounded-xl active:scale-95 transition-transform" 
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t('common.contacts')}
                </Link>
              </div>
              
              {user ? (
                <>
                  <div className="border-t my-2 pt-2">
                    <div className="grid grid-cols-2 gap-2">
                      <Link to={`/${currentLang}/profile`} className="py-2.5 px-3 text-sm text-gray-600 bg-gray-50 rounded-xl flex items-center gap-2" onClick={() => setMobileMenuOpen(false)}>
                        <User className="w-4 h-4" />
                        {t('common.myProfile') || 'Профиль'}
                      </Link>
                      <Link to={`/${currentLang}/orders`} className="py-2.5 px-3 text-sm text-gray-600 bg-gray-50 rounded-xl flex items-center gap-2" onClick={() => setMobileMenuOpen(false)}>
                        <Package className="w-4 h-4" />
                        {t('common.myOrders')}
                      </Link>
                      <Link to={`/${currentLang}/addresses`} className="py-2.5 px-3 text-sm text-gray-600 bg-gray-50 rounded-xl flex items-center gap-2" onClick={() => setMobileMenuOpen(false)}>
                        <MapPin className="w-4 h-4" />
                        {t('common.myAddresses')}
                      </Link>
                      <Link to={`/${currentLang}/settings`} className="py-2.5 px-3 text-sm text-gray-600 bg-gray-50 rounded-xl flex items-center gap-2" onClick={() => setMobileMenuOpen(false)}>
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/><circle cx="12" cy="12" r="3"/></svg>
                        {t('common.settings') || 'Настройки'}
                      </Link>
                    </div>
                  </div>
                  {['Admin', 'Manager', 'Support', 'Courier', 'ContentCreator'].includes(user.role) && (
                    <Link to={`/${currentLang}/admin`} className="py-2.5 px-4 text-sm text-emerald-700 font-medium bg-emerald-50 rounded-xl text-center" onClick={() => setMobileMenuOpen(false)}>
                      {t('common.adminPanel')}
                    </Link>
                  )}
                  <button onClick={() => { onLogout(); setMobileMenuOpen(false); }} className="py-2.5 px-4 text-sm text-red-600 bg-red-50 rounded-xl flex items-center justify-center gap-2 mt-2">
                    <LogOut className="w-4 h-4" />
                    {t('common.logout')}
                  </button>
                </>
              ) : (
                <Link to={`/${currentLang}/login`} className="py-3 px-4 text-sm text-white font-medium bg-emerald-700 rounded-xl text-center active:scale-95 transition-transform" onClick={() => setMobileMenuOpen(false)}>
                  {t('common.login')}
                </Link>
              )}
            </nav>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-2 sm:px-4 py-2 sm:py-6">
        <h1 className="sr-only">{t('catalog.pageHeading')}</h1>
        <div className="flex gap-6">
          {/* Sidebar Filters - Desktop */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <div className="sticky top-24 space-y-6">
              {/* Categories */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">{t('catalog.categories')}</h3>
                <div className="space-y-1">
                  {CATEGORIES.map(cat => (
                    <button
                      key={cat.id}
                      onClick={() => setSelectedCategory(cat.id)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                        selectedCategory === cat.id
                          ? 'bg-stone-100 text-stone-800 font-medium'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      {cat.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Price Range */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">{t('catalog.price')}</h3>
                <div className="px-2">
                  <Slider
                    value={priceRange}
                    min={0}
                    max={maxPrice}
                    step={100}
                    onValueChange={setPriceRange}
                    className="mb-3"
                  />
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>{formatPrice(priceRange[0])}</span>
                    <span>{formatPrice(priceRange[1])}</span>
                  </div>
                </div>
              </div>

              {/* B2B Notice */}
              {user?.role === 'B2B' && (
                <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-sm text-green-800">
                    <span className="font-medium">{t('catalog.b2bNotice')}</span><br />
                    {t('catalog.b2bPrices')}
                  </p>
                </div>
              )}
            </div>
          </aside>

          {/* Products */}
          <div className="flex-1 min-w-0">
            {/* Toolbar - Compact on mobile */}
            <div className="flex flex-wrap items-center justify-between gap-2 sm:gap-4 mb-3 sm:mb-6">
              <div className="flex items-center gap-2">
                {/* Mobile Filters Button */}
                <Button
                  variant="outline"
                  size="sm"
                  className="lg:hidden h-9 px-3 rounded-xl"
                  onClick={() => setMobileFiltersOpen(true)}
                >
                  <Filter className="w-4 h-4 mr-1.5" />
                  {t('catalog.filters')}
                </Button>
                
                <span className="text-xs sm:text-sm text-gray-600">
                  {displayProducts.length} {t('catalog.products')}
                </span>
              </div>

              <div className="flex items-center gap-2">
                {/* Sort - Compact on mobile */}
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-[140px] sm:w-[180px] h-9 text-xs sm:text-sm rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SORT_OPTIONS.map(opt => (
                      <SelectItem key={opt.id} value={opt.id}>{opt.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* View Mode */}
                <div className="hidden sm:flex border rounded-lg">
                  <Button
                    variant={viewMode === 'grid' ? 'default' : 'ghost'}
                    size="sm"
                    className="rounded-r-none"
                    onClick={() => setViewMode('grid')}
                  >
                    <Grid3X3 className="w-4 h-4" />
                  </Button>
                  <Button
                    variant={viewMode === 'list' ? 'default' : 'ghost'}
                    size="sm"
                    className="rounded-l-none"
                    onClick={() => setViewMode('list')}
                  >
                    <List className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Category Pills - Mobile - Improved */}
            <div className="flex gap-2 overflow-x-auto pb-3 lg:hidden scrollbar-hide">
              {CATEGORIES.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`flex-shrink-0 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm whitespace-nowrap transition-all active:scale-95 ${
                    selectedCategory === cat.id
                      ? 'bg-emerald-700 text-white font-medium'
                      : 'bg-white text-gray-600 border hover:border-emerald-600'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            {/* Favorites Section */}
            {user && favoriteProducts.length > 0 && (
              <div className="mb-6" data-testid="favorites-section">
                <div className="flex items-center justify-between mb-3">
                  <button 
                    onClick={() => setShowFavorites(!showFavorites)}
                    className="flex items-center gap-2 text-base font-semibold text-gray-800 hover:text-emerald-700 transition-colors"
                    data-testid="favorites-toggle"
                  >
                    <Heart className="w-5 h-5 fill-red-500 text-red-500" />
                    <span>{t('catalog.favorites', 'Избранное')}</span>
                    <Badge variant="secondary" className="ml-1">{favoriteProducts.length}</Badge>
                    <ChevronDown className={`w-4 h-4 transition-transform ${showFavorites ? 'rotate-180' : ''}`} />
                  </button>
                </div>
                {showFavorites && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3 lg:gap-4 mb-4 p-3 bg-red-50/50 rounded-lg border border-red-100">
                    {favoriteProducts.map((product) => (
                      <div key={`fav-${product.id}`} className="bg-white rounded-lg overflow-hidden border hover:shadow-lg transition-shadow">
                        <div className="aspect-square bg-gray-100 relative">
                          {product.image_url ? (
                            <img src={product.image_url} alt={getProductName(product)} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center"><Package className="w-8 h-8 text-gray-300" /></div>
                          )}
                          <button onClick={(e) => toggleFavorite(product.id, e)} className="absolute top-1 right-1 sm:top-2 sm:right-2 p-1 rounded-full bg-white/80 hover:bg-white">
                            <Heart className="w-4 h-4 sm:w-5 sm:h-5 fill-red-500 text-red-500" />
                          </button>
                        </div>
                        <div className="p-1.5 sm:p-3">
                          <h3 className="font-bold text-[13px] sm:text-sm text-gray-900 line-clamp-2 leading-tight mb-0.5">{getProductName(product)}</h3>
                          <p className="text-[9px] sm:text-xs text-gray-500 mb-1">{getUnitShortName(product.unit_of_measure)}</p>
                          <div className="flex items-center justify-between">
                            <span className="text-sm sm:text-base font-bold text-emerald-700">{(product.final_price || product.retail_price).toLocaleString()} {t('common.currency')}</span>
                            <Button size="sm" className="h-7 w-7 p-0 sm:h-8 sm:w-8 bg-emerald-700 hover:bg-emerald-800" onClick={() => addToCart(product)}>
                              <Plus className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Products Grid */}
            {displayProducts.length === 0 ? (
              <div className="text-center py-12 text-gray-500" data-testid="catalog-empty-state">
                <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
                {loadFailed ? (
                  <>
                    <p className="mb-1 font-medium text-gray-700">{t('catalog.loadFailedTitle')}</p>
                    <p className="text-sm mb-4">{t('catalog.loadFailedHint')}</p>
                    <Button
                      onClick={() => fetchProducts(0)}
                      className="bg-emerald-700 hover:bg-emerald-800"
                      data-testid="catalog-retry-btn"
                    >
                      <Loader2 className="w-4 h-4 mr-2" />
                      {t('catalog.retry')}
                    </Button>
                  </>
                ) : (
                  <p>{t('catalog.noProducts')}</p>
                )}
              </div>
            ) : viewMode === 'grid' ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3 lg:gap-4">
                {displayProducts.map((product) => {
                  const variants = variantMap[product.id];
                  const hasVariants = variants && variants.length > 0;
                  const activeProduct = getActiveProduct(product);
                  const isParentSelected = !selectedVariants[product.id] || selectedVariants[product.id] === product.id;

                  return (
                  <div
                    key={product.id}
                    className="bg-white rounded-lg overflow-hidden border hover:shadow-lg transition-shadow"
                    data-testid={`product-card-${product.id}`}
                  >
                    {/* Product Image */}
                    <div
                      className="aspect-square bg-gray-100 relative overflow-hidden group cursor-pointer"
                      onClick={() => setDetailProduct(activeProduct)}
                      data-testid={`product-image-${activeProduct.id}`}
                    >
                      {activeProduct.image_url ? (
                        <>
                          <img
                            src={activeProduct.image_url}
                            alt={getProductName(activeProduct)}
                            className={`w-full h-full object-cover transition-opacity duration-300 ${activeProduct.hover_image_url ? 'group-hover:opacity-0' : ''}`}
                          />
                          {activeProduct.hover_image_url && (
                            <img
                              src={activeProduct.hover_image_url}
                              alt={`${getProductName(activeProduct)} - hover`}
                              className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                            />
                          )}
                        </>
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Package className="w-8 sm:w-12 h-8 sm:h-12 text-gray-300" />
                        </div>
                      )}
                      {activeProduct.discount_applied && (
                        <Badge className="absolute top-1 left-1 sm:top-2 sm:left-2 bg-green-500 text-[10px] sm:text-xs px-1 sm:px-2 py-0.5 z-10">
                          {t('catalog.specialPrice')}
                        </Badge>
                      )}
                      {user && (
                        <button
                          onClick={(e) => toggleFavorite(activeProduct.id, e)}
                          className="absolute top-1 right-1 sm:top-2 sm:right-2 p-1 rounded-full bg-white/80 hover:bg-white transition-colors z-10"
                          data-testid={`favorite-btn-${activeProduct.id}`}
                        >
                          <Heart className={`w-4 h-4 sm:w-5 sm:h-5 ${favoriteIds.has(activeProduct.id) ? 'fill-red-500 text-red-500' : 'text-gray-400'}`} />
                        </button>
                      )}
                    </div>

                    {/* Product Info */}
                    <div className="p-1.5 sm:p-3">
                      <h3
                        className="font-bold text-[13px] sm:text-sm text-gray-900 line-clamp-2 leading-tight mb-0.5 cursor-pointer hover:text-emerald-700 transition-colors"
                        onClick={() => setDetailProduct(activeProduct)}
                      >
                        {getProductName(activeProduct)}
                      </h3>
                      <p className="text-[9px] sm:text-xs text-gray-500 mb-1 sm:mb-2">
                        {getUnitShortName(activeProduct.unit_of_measure)}
                      </p>

                      {/* Variant selector buttons */}
                      {hasVariants && (
                        <div className="flex flex-wrap gap-1 mb-1.5 sm:mb-2" data-testid={`variant-selector-${product.id}`}>
                          {product.variant_label && (
                            <button
                              onClick={() => setSelectedVariants(prev => ({...prev, [product.id]: product.id}))}
                              className={`px-1.5 sm:px-2 py-0.5 rounded text-[9px] sm:text-xs font-medium border transition-colors ${
                                isParentSelected
                                  ? 'bg-emerald-700 text-white border-emerald-700'
                                  : 'bg-white text-gray-600 border-gray-200 hover:border-emerald-300'
                              }`}
                            >
                              {product.variant_label}
                            </button>
                          )}
                          {variants.map(v => (
                            <button
                              key={v.id}
                              onClick={() => setSelectedVariants(prev => ({...prev, [product.id]: v.id}))}
                              className={`px-1.5 sm:px-2 py-0.5 rounded text-[9px] sm:text-xs font-medium border transition-colors ${
                                selectedVariants[product.id] === v.id
                                  ? 'bg-emerald-700 text-white border-emerald-700'
                                  : 'bg-white text-gray-600 border-gray-200 hover:border-emerald-300'
                              }`}
                            >
                              {v.variant_label || getProductName(v)}
                            </button>
                          ))}
                        </div>
                      )}
                      
                      <div className="flex items-center justify-between">
                        <div className="min-w-0">
                          {activeProduct.discount_applied ? (
                            <div className="flex flex-col">
                              <span className="text-xs sm:text-lg font-bold text-green-600">
                                {formatPrice(getProductPrice(activeProduct))}
                              </span>
                              <span className="text-[9px] sm:text-xs text-gray-400 line-through hidden sm:block">
                                {formatPrice(activeProduct.retail_price)}
                              </span>
                            </div>
                          ) : (
                            <span className="text-xs sm:text-lg font-bold text-gray-900">
                              {formatPrice(getProductPrice(activeProduct))}
                            </span>
                          )}
                        </div>
                        {/* Mobile: Compact round add button */}
                        <Button
                          size="sm"
                          className="sm:hidden bg-emerald-700 hover:bg-emerald-800 h-7 w-7 min-w-7 p-0 rounded-full flex-shrink-0"
                          onClick={() => addToCart(activeProduct, 1)}
                          data-testid={`add-to-cart-mobile-${activeProduct.id}`}
                        >
                          <ShoppingCart className="w-3.5 h-3.5" />
                        </Button>
                        {/* Desktop: Quantity selector + add button */}
                        <div className="hidden sm:flex items-center gap-1">
                          <div className="flex items-center border rounded-lg overflow-hidden">
                            <button
                              className="w-7 h-8 flex items-center justify-center hover:bg-gray-100 text-gray-600"
                              onClick={() => setProductQuantity(activeProduct.id, getProductQuantity(activeProduct.id) - 1)}
                            >
                              <Minus className="w-3 h-3" />
                            </button>
                            <input
                              type="text"
                              inputMode="numeric"
                              value={getProductQuantity(activeProduct.id)}
                              onChange={(e) => setProductQuantity(activeProduct.id, e.target.value)}
                              className="w-8 h-8 text-center text-base font-medium border-x focus:outline-none"
                              data-testid={`product-quantity-${activeProduct.id}`}
                            />
                            <button
                              className="w-7 h-8 flex items-center justify-center hover:bg-gray-100 text-gray-600"
                              onClick={() => setProductQuantity(activeProduct.id, getProductQuantity(activeProduct.id) + 1)}
                            >
                              <Plus className="w-3 h-3" />
                            </button>
                          </div>
                          <Button
                            size="sm"
                            className="bg-emerald-700 hover:bg-emerald-800 h-8 w-8 p-0"
                            onClick={() => addToCart(activeProduct, getProductQuantity(activeProduct.id))}
                            data-testid={`add-to-cart-${activeProduct.id}`}
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            ) : (
              /* List View */
              <div className="space-y-3">
                {displayProducts.map((product) => {
                  const activeProduct = getActiveProduct(product);
                  const variants = variantMap[product.id];
                  const hasVariants = variants && variants.length > 0;
                  const isParentSelected = !selectedVariants[product.id] || selectedVariants[product.id] === product.id;

                  return (
                  <div
                    key={product.id}
                    className="bg-white rounded-lg border p-4 flex gap-4 hover:shadow-md transition-shadow"
                  >
                    <div
                      className="w-24 h-24 flex-shrink-0 bg-gray-100 rounded-lg overflow-hidden group cursor-pointer"
                      onClick={() => setDetailProduct(activeProduct)}
                      data-testid={`product-image-list-${activeProduct.id}`}
                    >
                      {activeProduct.image_url ? (
                        <div className="relative w-full h-full">
                          <img 
                            src={activeProduct.image_url} 
                            alt={getProductName(activeProduct)} 
                            className={`w-full h-full object-cover transition-opacity duration-300 ${activeProduct.hover_image_url ? 'group-hover:opacity-0' : ''}`} 
                          />
                          {activeProduct.hover_image_url && (
                            <img
                              src={activeProduct.hover_image_url}
                              alt={`${getProductName(activeProduct)} - hover`}
                              className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                            />
                          )}
                        </div>
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Package className="w-8 h-8 text-gray-300" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <h3 className="font-medium text-gray-900 cursor-pointer hover:text-emerald-700 transition-colors" onClick={() => setDetailProduct(activeProduct)}>{getProductName(activeProduct)}</h3>
                        {user && (
                          <button onClick={(e) => toggleFavorite(activeProduct.id, e)} className="p-1 ml-2 shrink-0" data-testid={`favorite-list-btn-${activeProduct.id}`}>
                            <Heart className={`w-5 h-5 ${favoriteIds.has(activeProduct.id) ? 'fill-red-500 text-red-500' : 'text-gray-300 hover:text-red-400'}`} />
                          </button>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mt-1">{getProductDescription(activeProduct)}</p>
                      {/* Variant buttons in list view */}
                      {hasVariants && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {product.variant_label && (
                            <button
                              onClick={() => setSelectedVariants(prev => ({...prev, [product.id]: product.id}))}
                              className={`px-2 py-0.5 rounded text-xs font-medium border transition-colors ${
                                isParentSelected ? 'bg-emerald-700 text-white border-emerald-700' : 'bg-white text-gray-600 border-gray-200 hover:border-emerald-300'
                              }`}
                            >{product.variant_label}</button>
                          )}
                          {variants.map(v => (
                            <button
                              key={v.id}
                              onClick={() => setSelectedVariants(prev => ({...prev, [product.id]: v.id}))}
                              className={`px-2 py-0.5 rounded text-xs font-medium border transition-colors ${
                                selectedVariants[product.id] === v.id ? 'bg-emerald-700 text-white border-emerald-700' : 'bg-white text-gray-600 border-gray-200 hover:border-emerald-300'
                              }`}
                            >{v.variant_label || getProductName(v)}</button>
                          ))}
                        </div>
                      )}
                      <div className="flex items-center justify-between mt-2">
                        <div>
                          {activeProduct.discount_applied ? (
                            <>
                              <span className="text-xl font-bold text-green-600">
                                {formatPrice(getProductPrice(activeProduct))}
                              </span>
                              <span className="text-sm text-gray-400 line-through ml-2">
                                {formatPrice(activeProduct.retail_price)}
                              </span>
                            </>
                          ) : (
                            <span className="text-xl font-bold text-gray-900">
                              {formatPrice(getProductPrice(activeProduct))}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="flex items-center border rounded-lg overflow-hidden">
                            <button
                              className="w-8 h-9 flex items-center justify-center hover:bg-gray-100 text-gray-600"
                              onClick={() => setProductQuantity(activeProduct.id, getProductQuantity(activeProduct.id) - 1)}
                            >
                              <Minus className="w-4 h-4" />
                            </button>
                            <input
                              type="text"
                              inputMode="numeric"
                              value={getProductQuantity(activeProduct.id)}
                              onChange={(e) => setProductQuantity(activeProduct.id, e.target.value)}
                              className="w-12 h-9 text-center font-medium border-x focus:outline-none"
                            />
                            <button
                              className="w-8 h-9 flex items-center justify-center hover:bg-gray-100 text-gray-600"
                              onClick={() => setProductQuantity(activeProduct.id, getProductQuantity(activeProduct.id) + 1)}
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                          </div>
                          <Button
                            className="bg-emerald-700 hover:bg-emerald-800"
                            onClick={() => addToCart(activeProduct, getProductQuantity(activeProduct.id))}
                          >
                            <ShoppingCart className="w-4 h-4 mr-2" />
                            {t('catalog.toCart')}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Mobile Filters Sheet - Full Screen */}
      <Sheet open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
        <SheetContent side="bottom" className="h-[85vh] rounded-t-3xl">
          <div className="w-12 h-1.5 bg-gray-300 rounded-full mx-auto mb-4" />
          <SheetHeader className="pb-4 border-b">
            <SheetTitle className="text-lg">{t('catalog.filters')}</SheetTitle>
          </SheetHeader>
          <div className="py-6 space-y-6 overflow-y-auto max-h-[calc(85vh-120px)]">
            {/* Categories */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">{t('catalog.categories')}</h3>
              <div className="grid grid-cols-2 gap-2">
                {CATEGORIES.map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => { setSelectedCategory(cat.id); setMobileFiltersOpen(false); }}
                    className={`text-left px-4 py-3 rounded-xl text-sm transition-all ${
                      selectedCategory === cat.id
                        ? 'bg-emerald-700 text-white font-medium'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200 active:scale-95'
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Price Range */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">{t('catalog.price')}</h3>
              <div className="px-2">
                <Slider
                  value={priceRange}
                  min={0}
                  max={maxPrice}
                  step={100}
                  onValueChange={setPriceRange}
                  className="mb-4"
                />
                <div className="flex justify-between text-sm text-gray-600 bg-gray-100 rounded-lg p-3">
                  <span>{formatPrice(priceRange[0])}</span>
                  <span className="text-gray-400">—</span>
                  <span>{formatPrice(priceRange[1])}</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Apply Button */}
          <div className="absolute bottom-0 left-0 right-0 p-4 bg-white border-t">
            <Button 
              className="w-full bg-emerald-700 hover:bg-emerald-800 h-12 text-base"
              onClick={() => setMobileFiltersOpen(false)}
            >
              {t('catalog.applyFilters') || 'Применить фильтры'}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Floating Cart Button - Mobile */}
      {cartCount > 0 && (
        <div className="fixed bottom-4 left-4 right-4 md:hidden z-40 animate-in slide-in-from-bottom-4">
          <button
            onClick={() => setCartOpen(true)}
            className="w-full bg-emerald-700 hover:bg-emerald-800 text-white rounded-2xl shadow-lg shadow-emerald-900/30 p-4 flex items-center justify-between active:scale-[0.98] transition-transform"
            data-testid="floating-cart-btn"
          >
            <div className="flex items-center gap-3">
              <div className="relative">
                <ShoppingCart className="w-6 h-6" />
                <span className="absolute -top-2 -right-2 bg-white text-emerald-700 text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {cartCount}
                </span>
              </div>
              <span className="font-medium">{t('cart.viewCart') || 'Корзина'}</span>
            </div>
            <span className="font-bold text-lg">{formatPrice(cartTotal)}</span>
          </button>
        </div>
      )}

      {/* Footer - Compact on Mobile */}
      <footer className={`bg-stone-900 text-white mt-12 ${cartCount > 0 ? 'pb-24 md:pb-0' : ''}`}>
        <div className="max-w-7xl mx-auto px-4 py-6 md:py-8">
          {/* Mobile Footer - Compact */}
          <div className="md:hidden">
            <div className="flex items-center justify-between mb-4">
              <img 
                src="/logo.jpg" 
                alt="Ainanan" 
                className="h-8 object-contain"
              />
              <div className="flex gap-4">
                <Link to={`/${currentLang}`} className="text-sm text-stone-300 hover:text-white">{t('common.catalog')}</Link>
                <Link to={`/${currentLang}/about`} className="text-sm text-stone-300 hover:text-white">{t('common.about')}</Link>
                <Link to={`/${currentLang}/contacts`} className="text-sm text-stone-300 hover:text-white">{t('common.contacts')}</Link>
              </div>
            </div>
            <div className="flex items-center justify-between text-xs text-stone-400 pt-3 border-t border-stone-700">
              <span>© {new Date().getFullYear()} Ainanan</span>
              <a href="https://wa.me/77010982754" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-stone-300 hover:text-green-400">
                <Phone className="w-3 h-3" />
                +7 701 098 27 54
              </a>
            </div>
          </div>
          
          {/* Desktop Footer - Full */}
          <div className="hidden md:block">
            <div className="grid grid-cols-3 gap-8">
              <div>
                <img 
                  src="/logo.jpg" 
                  alt="Ainanan" 
                  className="h-12 object-contain mb-4"
                />
                <p className="text-sm text-stone-300">
                  {t('footer.description')}
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-white mb-4">{t('footer.navigation')}</h4>
                <div className="space-y-2">
                  <Link to={`/${currentLang}`} className="block text-sm text-stone-300 hover:text-white">{t('common.catalog')}</Link>
                  <Link to={`/${currentLang}/about`} className="block text-sm text-stone-300 hover:text-white">{t('common.about')}</Link>
                  <Link to={`/${currentLang}/contacts`} className="block text-sm text-stone-300 hover:text-white">{t('common.contacts')}</Link>
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-white mb-4">{t('footer.contacts')}</h4>
                <div className="space-y-2 text-sm text-stone-300">
                  <a href="https://wa.me/77010982754" target="_blank" rel="noopener noreferrer" className="block hover:text-green-400 transition-colors">+7 701 098 27 54</a>
                  <a href="mailto:admin@ainanan.kz" className="block hover:text-white transition-colors">admin@ainanan.kz</a>
                  <p>{t('footer.city')}</p>
                </div>
              </div>
            </div>
            <div className="border-t border-stone-700 mt-8 pt-6 text-center text-sm text-stone-400">
              © {new Date().getFullYear()} Ainanan. {t('common.allRights')}.
            </div>
          </div>
        </div>
      </footer>

      {/* Product Details Modal */}
      <Dialog open={!!detailProduct} onOpenChange={(open) => !open && setDetailProduct(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="product-detail-modal">
          {detailProduct && (
            <>
              <DialogHeader>
                <DialogTitle className="text-xl sm:text-2xl pr-6" data-testid="product-detail-name">
                  {getProductName(detailProduct)}
                </DialogTitle>
                <DialogDescription className="text-xs text-gray-500">
                  {getUnitShortName(detailProduct.unit_of_measure)}
                </DialogDescription>
              </DialogHeader>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center">
                  {detailProduct.image_url ? (
                    <img
                      src={detailProduct.image_url}
                      alt={getProductName(detailProduct)}
                      className="w-full h-full object-cover"
                      data-testid="product-detail-image"
                    />
                  ) : (
                    <Package className="w-16 h-16 text-gray-300" />
                  )}
                </div>
                <div className="flex flex-col">
                  {getProductDescription(detailProduct) ? (
                    <p className="text-sm text-gray-600 whitespace-pre-line mb-4" data-testid="product-detail-description">
                      {getProductDescription(detailProduct)}
                    </p>
                  ) : (
                    <p className="text-sm text-gray-400 italic mb-4">{t('catalog.noDescription')}</p>
                  )}
                  <div className="mt-auto space-y-3">
                    <div className="flex items-baseline gap-2">
                      {detailProduct.discount_applied ? (
                        <>
                          <span className="text-2xl font-bold text-green-600">{formatPrice(getProductPrice(detailProduct))}</span>
                          <span className="text-sm text-gray-400 line-through">{formatPrice(detailProduct.retail_price)}</span>
                        </>
                      ) : (
                        <span className="text-2xl font-bold text-gray-900">{formatPrice(getProductPrice(detailProduct))}</span>
                      )}
                    </div>
                    <Button
                      className="w-full bg-emerald-700 hover:bg-emerald-800"
                      onClick={() => { addToCart(detailProduct, 1); setDetailProduct(null); }}
                      data-testid="product-detail-add-to-cart"
                    >
                      <ShoppingCart className="w-4 h-4 mr-2" />
                      {t('catalog.toCart')}
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Catalog;
